
import {computed, ComputedRef, defineComponent, onMounted, ref, Ref, watch} from "vue";
import {useStore} from "vuex";
import {dispatchError} from "../../utils/error_handling";
import {Content, ContentAnnotation, secondsToCustomFormat} from "./helpers";
import {ContentType} from "../../../../domain/entities/content/content";

export default defineComponent({
  name: "ContentAnnotationList",
  setup() {
    const store = useStore();

    const currentContent: ComputedRef<Content> = computed(() => store.getters["content_module/currentContent"]);

    onMounted(async () => await onCurrentContentUpdate());

    watch(currentContent, async () => await onCurrentContentUpdate())

    const onCurrentContentUpdate = async () => {
      try {
        await loadAnnotations();
        resizeAllTextarea();
      } catch (e) {
        console.error("[onMounted] Error ", e);
        await dispatchError(e);
      }
    };

    const loadAnnotations = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/annotation/loadAnnotations");
      } catch (e) {
        console.error("[loadAnnotations] Error ", e);
        throw e;
      }
    };

    const resizeAllTextarea = (): void => {
      const textareaCollection: NodeListOf<HTMLTextAreaElement> = document.querySelectorAll("[data-textarea]");
      for (let i = 0; i < textareaCollection.length; i++) {
        resizeTextarea(textareaCollection[i])
      }
    };

    /** Makes the textarea in focus resize to fit the content.*/
    function resizeTextarea(textarea: HTMLTextAreaElement) {
      textarea.style.height = "fit-content";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }


    const annotations: ComputedRef<ContentAnnotation[]> = computed(
        () => store.getters["content_module/annotation/annotations"]
    );

    watch(annotations, () => {
      setTimeout(resizeAllTextarea, 1);
    })

    const formatContentPosition = (annotation: ContentAnnotation): string => {
      switch (currentContent.value.type) {
        case ContentType.YOUTUBE:
        case ContentType.MP3:
          if (!annotation.contentTimestamp) return "";
          return secondsToCustomFormat(annotation.contentTimestamp, "hh:mm:ss")
        default:
          return "";
      }
    };

    const selectedIndex: Ref<number> = ref(-1);

    const onClickAnnotation = async (index: number, _: ContentAnnotation): Promise<void> => {
      try {
        if (selectedIndex.value === index) return;
        await selectAnnotation(index);
      } catch (e) {
        console.error("[onClickAnnotation] Error ", e)
        await dispatchError(e);
      }
    };

    const selectAnnotation = async (index: number): Promise<void> => {
      try {
        await store.dispatch("content_module/annotation/selectAnnotation", index);
      } catch (e) {
        console.error("[selectAnnotation] Error ", e);
        throw e;
      }
    };

    const getSelectedTextareaByIndex = (index: number) => {
      const textarea = document.querySelector(`[data-textarea="${index}"]`) as HTMLTextAreaElement;
      if (!textarea) throw new Error(`Textarea of index '${index}' were not found!`);
      return textarea;
    }

    const onClickUpdateAnnotation = async (index: number, annotation: ContentAnnotation) => {
      try {
        selectedIndex.value = index;
        const textarea = getSelectedTextareaByIndex(index);
        textarea.focus();
        await store.dispatch("content_module/annotation/takingNotes", annotation);
      } catch (e) {
        console.error("[onClickUpdateAnnotation] Error ", e);
        await dispatchError(e);
      }
    };


    const onInputAnnotation = async (event: Event, index: number, annotation: ContentAnnotation): Promise<void> => {
      try {
        if (!event.target) return;
        const textarea: HTMLTextAreaElement = event.target as HTMLTextAreaElement;
        resizeTextarea(textarea);
        annotation.text = textarea.value;
        await store.dispatch("content_module/annotation/takingNotes", annotation);
      } catch (e) {
        console.error("[onInputAnnotation] Error ", e);
        await dispatchError(e)
      }
    };

    const onBlurAnnotation = async (event: Event, index: number, annotation: ContentAnnotation): Promise<void> => {
      try {
        const aux = selectedIndex.value;
        console.log(index, aux)
        selectedIndex.value = -1;
        if (aux === index) {
          await updateAnnotation(index, annotation);
        }
      } catch (e) {
        console.error("[onBlurAnnotation] Error ", e);
        await dispatchError(e)
      }
    };

    const updateAnnotation = async (index: number, annotation: ContentAnnotation): Promise<void> => {
      try {
        await store.dispatch("content_module/annotation/updateAnnotation", {index, text: annotation.text});
      } catch (e) {
        console.error("[updateAnnotation] Error ", e);
        throw e;
      }
    };

    const onClickDeleteAnnotation = async (index: number, _: ContentAnnotation): Promise<void> => {
      try {
        await deleteAnnotation(index);
        resizeAllTextarea();
      } catch (e) {
        console.error("[onClickDeleteAnnotation] Error ", e);
        await dispatchError(e);
      }
    };

    const deleteAnnotation = async (index: number): Promise<void> => {
      try {
        await store.dispatch("content_module/annotation/deleteAnnotation", index);
      } catch (e) {
        console.error("[deleteAnnotation] Error ", e);
        throw e;
      }
    };

    return {
      annotations,
      selectedIndex,
      formatContentPosition,
      onClickAnnotation,
      onClickUpdateAnnotation,
      onInputAnnotation,
      onBlurAnnotation,
      onClickDeleteAnnotation,
    };
  }
});
